import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createSubscriptionAPI, fetchSubscriptionDataByIdAPI, fetchSubscriptionListAPI, getUserSubscriptionPlanAPI, getUserSubscriptionPlanHistory, updateSubscriptionAPI, updateSubscriptionStatusAPI, createSubscriptionPermission,getSubscriptionPermissionDetails, makeDefaultSubscriptionAPI } from "../apis/subscription.api";
import { successToastify } from "../utils/toastify.util";

const baseFields = {
    name: "",
    description: "",
    plan_type: "monthly",
    price_monthly: null,
    price_annually: null,
    trial_period: 0,
    id: "",
    is_ads: 0,
    max_saved_hunt: 0,
    max_hunts: null,
    max_challenges: null,
    max_hunt_items: null,
    max_challenge_items: null,
    max_created_items: null,
    max_purchased_items: null,
    max_hunt_teams: null,
    is_relaunch: 0,
    is_relaunch_quests: 0,
    max_share: 0,
    video_available: 1,
    userPlanHistory: [],
    isHistoryLoad: false
};

const initialState = {
    isLoad: false,
    isBtnLoad: false,
    isMakeDefaultBtnLoad: false,
    listData: [],
    userSubscriptionList: [],
    isRedirect: false,
    isUpdate: false,
    detailsData: null,
    fields: baseFields,

    mappedUserIds: [],
    isMapUsersLoad: false,
    isMapUsersBtnLoad: false,
}

export const fetchList = createAsyncThunk(
    "subscription/fetchList",
    async (payload) => {
        try {
            const response = await fetchSubscriptionListAPI(payload);
            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
)

export const createSubscription = createAsyncThunk(
    "subscription/createSubscription",
    async (payload, thunkAPI) => {
        try {
            const response = await createSubscriptionAPI(payload);
            if (response.success) {
                let req = {
                    limit: 10,
                    offset: 0,
                    searchText: ""
                }
                thunkAPI.dispatch(fetchList(req));
            }
            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
)

export const updateSubscription = createAsyncThunk(
    "subscription/updateSubscription",
    async (payload, thunkAPI) => {
        try {
            const response = await updateSubscriptionAPI(payload);
            if (response.success) {
                let req = {
                    limit: 10,
                    offset: 0,
                    searchText: ""
                }
                thunkAPI.dispatch(fetchList(req));
            }
            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
)

export const fetchSubscriptionDataById = createAsyncThunk(
    "subscription/fetchSubscriptionDataById",
    async (payload) => {
        try {
            const response = await fetchSubscriptionDataByIdAPI(payload);
            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
)

export const updateSubscriptionStatus = createAsyncThunk(
    "subscription/updateSubscriptionStatus",
    async (payload, thunkAPI) => {
        try {
            const response = await updateSubscriptionStatusAPI(payload);
            if (response.success) {
                if (payload.status === 1) {
                    successToastify("Subscription activated successfully");
                } else if (payload.status === 0) {
                    successToastify("Subscription deactivated successfully");
                } else {
                    successToastify("Subscription deleted successfully");
                }
                let req = {
                    limit: 10,
                    offset: 0,
                    searchText: ""
                }
                thunkAPI.dispatch(fetchList(req));
            }
            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
)

export const makeDefaultSubscription = createAsyncThunk(
    "subscription/makeDefaultSubscription",
    async (payload, thunkAPI) => {
        try {
            const response = await makeDefaultSubscriptionAPI({ subscriptionid: payload.id });
            if (response.success) {
                successToastify(response.message || "Default plan updated successfully");
                thunkAPI.dispatch(
                    fetchList({
                        limit: payload.limit ?? 10,
                        offset: payload.offset ?? 0,
                        searchText: payload.searchText ?? "",
                    })
                );
            }
            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
)

export const getUserSubscriptionList = createAsyncThunk(
    "subscription/getUserSubscriptionList",
    async (payload) => {
        try {
            const response = await getUserSubscriptionPlanAPI(payload);
            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
)

export const fetchUserSubscriptionHistory = createAsyncThunk(
    "subscription/fetchUserSubscriptionHistory",
    async (payload) => {
        try {
            const response = await getUserSubscriptionPlanHistory(payload);
            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
)


export const fetchSubscriptionMappedUsers = createAsyncThunk(
    "subscription/fetchSubscriptionMappedUsers",
    async (payload) => {
        try {
            const response = await getSubscriptionPermissionDetails(payload);
            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
);

export const mapUsersToSubscription = createAsyncThunk(
    "subscription/mapUsersToSubscription",
    async (payload, thunkAPI) => {
        try {
            const response = await createSubscriptionPermission(payload);

            // ✅ optional: refresh list or refresh mapped users
            if (response.success || response.status === 1) {
                successToastify(response.message || "Users mapped successfully");
                // refresh mapped user ids in modal
                thunkAPI.dispatch(
                    fetchSubscriptionMappedUsers({ subscriptionid: payload.subscriptionid })
                );
            }

            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
);




const subscriptionSlice = createSlice({
    name: "subscription",
    initialState,
    reducers: {
        setRedirect: (state, action) => {
            state.isRedirect = action.payload;
        },
        setUpdate: (state, action) => {
            state.isUpdate = action.payload;
        },
        setPlanDetails: (state, action) => {
            state.detailsData = action.payload;
        },
        setFields: (state, action) => {
            state.fields = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchList.pending, (state) => {
            state.isLoad = true;
            state.listData = [];
        });
        builder.addCase(fetchList.fulfilled, (state, action) => {
            state.isLoad = false;
            state.isRedirect = false;
            state.listData = action.payload.result;
        });

        builder.addCase(createSubscription.pending, (state) => {
            state.isBtnLoad = true;
        });
        builder.addCase(createSubscription.fulfilled, (state) => {
            state.isBtnLoad = false;
            state.isRedirect = true;
        });

        builder.addCase(updateSubscription.pending, (state) => {
            state.isBtnLoad = true;
        });
        builder.addCase(updateSubscription.fulfilled, (state) => {
            state.isBtnLoad = false;
            state.isRedirect = true;
        });

        builder.addCase(makeDefaultSubscription.pending, (state) => {
            state.isMakeDefaultBtnLoad = true;
        });
        builder.addCase(makeDefaultSubscription.fulfilled, (state) => {
            state.isMakeDefaultBtnLoad = false;
        });

        builder.addCase(fetchSubscriptionDataById.pending, (state) => {
            state.isLoad = true;
        });
        builder.addCase(fetchSubscriptionDataById.fulfilled, (state, action) => {
            state.isLoad = false;
            const result = action.payload?.result || {};
            const rawTrialPeriod =
                result?.trial_period ?? result?.trialPeriod ?? result?.trialperiod;
            const normalizedTrialPeriod = Number.isFinite(Number(rawTrialPeriod))
                ? Number(rawTrialPeriod)
                : 0;
            const normalizedMaxSavedHunt = Number.isFinite(Number(result?.max_saved_hunt))
                ? Number(result?.max_saved_hunt)
                : 0;
            const rawPlanType = String(result?.plan_type ?? "").toLowerCase();
            const normalizedPlanType =
                rawPlanType === "y" || rawPlanType === "yearly"
                    ? "yearly"
                    : "monthly";

            state.fields = {
                ...baseFields,
                ...result,
                plan_type: normalizedPlanType,
                max_saved_hunt: normalizedMaxSavedHunt,
                trial_period: normalizedTrialPeriod,
            };
        });

        builder.addCase(getUserSubscriptionList.pending, (state) => {
            state.isLoad = true;
            state.userSubscriptionList = [];
        });
        builder.addCase(getUserSubscriptionList.fulfilled, (state, action) => {
            state.isLoad = false;
            state.isRedirect = false;
            state.userSubscriptionList = action.payload.result;
        });

        builder.addCase(fetchUserSubscriptionHistory.pending, (state) => {
            state.isHistoryLoad = true;
            state.userPlanHistory = [];
        });
        builder.addCase(fetchUserSubscriptionHistory.fulfilled, (state, action) => {
            state.isHistoryLoad = false;
            state.userPlanHistory = action.payload.result;
        });

        builder.addCase(fetchSubscriptionMappedUsers.pending, (state) => {
            state.isMapUsersLoad = true;
            state.mappedUserIds = [];
        });

        builder.addCase(fetchSubscriptionMappedUsers.fulfilled, (state, action) => {
            state.isMapUsersLoad = false;

            // ✅ Handle different backend shapes safely
            // expecting: action.payload.result = [1,2] OR { userids:[1,2] }
            const res = action.payload?.response;
            console.log("raction.payload?s" , action.payload);

            console.log("res" , res);

            if (Array.isArray(res)) {
                state.mappedUserIds = res;
            } else if (Array.isArray(res?.userids)) {
                state.mappedUserIds = res.userids;
            } else if (Array.isArray(res?.userIds)) {
                state.mappedUserIds = res.userIds;
            } else {
                state.mappedUserIds = [];
            }
        });

        builder.addCase(mapUsersToSubscription.pending, (state) => {
            state.isMapUsersBtnLoad = true;
        });

        builder.addCase(mapUsersToSubscription.fulfilled, (state) => {
            state.isMapUsersBtnLoad = false;
        });
    }
})

export const { setRedirect, setUpdate, setPlanDetails, setFields } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
